<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-6 pb-0">
      <div class="flex items-center gap-4 mb-4">
        <button @click="openAddUserModal" class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500">{{ $t('an.gestione_contatti.add_button') }}</button>
        <input 
          v-model="searchTerm" 
          @keyup.enter="performSearch"
          @blur="performSearch"
          type="text" 
          :placeholder="$t('an.gestione_contatti.search_placeholder')" 
          class="p93__input_general px-4 py-2 min-w-[240px]" 
        />
      </div>
      <table class="min-w-full bg-white rounded-md shadow-lg">
        <thead>
          <tr>
            <SortableTableHeader 
              :title="$t('an.fields.id')" 
              field="Contact" 
              :currentSortField="sortField" 
              :sortDirection="sortDirection" 
              @sort="sort" 
            />
            <SortableTableHeader 
              :title="$t('an.fields.company_name')" 
              field="CompanyName" 
              :currentSortField="sortField" 
              :sortDirection="sortDirection" 
              :isDefault="true"
              @sort="sort" 
            />
            <th class="py-2 px-4 border-b text-center">{{ $t('an.fields.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users.data" :key="user.id">
            <td class="py-2 px-4 border-b text-left">{{ user.Contact }}</td>
            <td class="py-2 px-4 border-b text-left">{{ user.CompanyName }}</td>
            <td class="py-2 px-4 border-b text-center">
              <button @click="openDetailsUserModal(user)" class="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 mr-2">{{ $t('an.actions.details') }}</button>
              <button v-if="$policy('editButton').canView" :disabled="$policy('editButton').props.disabled" @click="openEditUserModal(user)" class="bg-yellow-400 disabled:bg-yellow-300 text-white px-2 py-1 rounded hover:bg-yellow-500 mr-2">{{ $t('an.actions.edit') }}</button>
            </td>
          </tr>
          <tr>
            <td class="text-left" colspan="4"><Pagination :links="users.links" /></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale per aggiungere/modificare utente -->
    <div v-if="showUserModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" @click="closeUserModal" style="padding-top: 80px;">
      <div class="bg-white p-6 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl mb-4">{{ isEditing ? $t('an.gestione_contatti.modal_edit_title') : $t('an.gestione_contatti.modal_add_title') }}</h2>
        <form @submit.prevent="isEditing ? updateUser() : addUser()">
          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.company_name') }} *</label>
              <input v-model="userForm.CompanyName" type="text" class="p93__input" maxlength="128" required>
              <div v-if="errors.CompanyName" class="text-red-800 text-sm mt-1">{{ errors.CompanyName }}</div>
            </div>
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.city') }}</label>
              <input v-model="userForm.City" type="text" class="p93__input" maxlength="64">
              <div v-if="errors.City" class="text-red-800 text-sm mt-1">{{ errors.City }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-4">
              <label class="block text-gray-700">{{ $t('an.fields.address') }}</label>
              <input v-model="userForm.Address" type="text" class="p93__input" maxlength="128">
              <div v-if="errors.Address" class="text-red-800 text-sm mt-1">{{ errors.Address }}</div>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.zip_code') }}</label>
              <input v-model="userForm.ZIPCode" type="text" class="p93__input" maxlength="10">
              <div v-if="errors.ZIPCode" class="text-red-800 text-sm mt-1">{{ errors.ZIPCode }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.county') }}</label>
              <input v-model="userForm.County" type="text" class="p93__input" maxlength="3">
              <div v-if="errors.County" class="text-red-800 text-sm mt-1">{{ errors.County }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.country') }}</label>
              <input v-model="userForm.ISOCountryCode" type="text" class="p93__input" maxlength="2" placeholder="IT">
              <div v-if="errors.ISOCountryCode" class="text-red-800 text-sm mt-1">{{ errors.ISOCountryCode }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.latitude') }}</label>
              <input v-model="userForm.Latitude" type="text" class="p93__input" maxlength="16">
              <div v-if="errors.Latitude" class="text-red-800 text-sm mt-1">{{ errors.Latitude }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.longitude') }}</label>
              <input v-model="userForm.Longitude" type="text" class="p93__input" maxlength="16">
              <div v-if="errors.Longitude" class="text-red-800 text-sm mt-1">{{ errors.Longitude }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.phone1') }}</label>
              <input v-model="userForm.Telephone1" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.Telephone1" class="text-red-800 text-sm mt-1">{{ errors.Telephone1}}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.phone2') }}</label>
              <input v-model="userForm.Telephone2" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.Telephone2" class="text-red-800 text-sm mt-1">{{ errors.Telephone2}}</div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('ui.email') }}</label>
              <input v-model="userForm.EMail" type="email" class="p93__input" maxlength="128">
              <div v-if="errors.EMail" class="text-red-800 text-sm mt-1">{{ errors.EMail }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.vat') }}</label>
              <input v-model="userForm.TaxIdNumber" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.TaxIdNumber" class="text-red-800 text-sm mt-1">{{ errors.TaxIdNumber }}</div>
            </div>
            <div class="mb-4 col-span-1">
              <label class="block text-gray-700">{{ $t('an.fields.fiscal_code') }}</label>
              <input v-model="userForm.FiscalCode" type="text" class="p93__input" maxlength="20">
              <div v-if="errors.FiscalCode" class="text-red-800 text-sm mt-1">{{ errors.FiscalCode }}</div>
            </div>
          </div>

          <div class="grid grid-cols-4 gap-4">
            <div class="mb-4 col-span-2">
              <label class="block text-gray-700">{{ $t('an.fields.agent') }}</label>
              <select v-model="userForm.SalesPerson" class="p93__input_general disabled:bg-gray-100" :disabled="fixedAgent">
                <option value="">{{ $t('an.actions.choose_agent') }}</option>
                <option v-for="agent in salesPeople" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
              </select>
              <div v-if="errors.SalesPerson" class="text-red-800 text-sm mt-1">{{ errors.SalesPerson }}</div>
            </div>
          </div>

          <div class="flex justify-end pt-4">
            <button type="button" @click="closeUserModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2">{{ $t('ui.cancel') }}</button>
            <button type="submit" class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500">{{ isEditing ? $t('an.actions.update') : $t('an.actions.add') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modale dettagli utente -->
    <div
      v-if="showUserDetailsModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeUserDetailsModal"
      style="padding-top: 80px;"
    >
      <div
      class="bg-white p-6 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      @click.stop
      >
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-4">
        <label class="block text-gray-700">{{ $t('an.fields.type') }}</label>
        <input v-model="userDetails.CustomerType" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-2">
        <label class="block text-gray-700">{{ $t('an.fields.company_name') }}</label>
        <input v-model="userDetails.CompanyName" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-2">
        <label class="block text-gray-700">{{ $t('an.fields.city') }}</label>
        <input v-model="userDetails.City" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-4">
        <label class="block text-gray-700">{{ $t('an.fields.address') }}</label>
        <input v-model="userDetails.Address" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.zip_code') }}</label>
        <input v-model="userDetails.ZIPCode" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.county') }}</label>
        <input v-model="userDetails.County" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.country') }}</label>
        <input v-model="userDetails.ISOCountryCode" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.latitude') }}</label>
          <input v-model="userDetails.Latitude" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.longitude') }}</label>
          <input v-model="userDetails.Longitude" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.phone1') }}</label>
        <input v-model="userDetails.Telephone1" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.phone2') }}</label>
        <input v-model="userDetails.Telephone2" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('ui.email') }}</label>
        <input v-model="userDetails.EMail" type="text" class="p93__input" disabled readonly>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.vat') }}</label>
        <input v-model="userDetails.TaxIdNumber" type="text" class="p93__input" disabled readonly>
        </div>
        <div class="mb-4 col-span-1">
        <label class="block text-gray-700">{{ $t('an.fields.fiscal_code') }}</label>
        <input v-model="userDetails.FiscalCode" type="text" class="p93__input" disabled readonly>
        </div>
      </div>

      <div class="grid grid-cols-4 gap-4">
        <div class="mb-4 col-span-2">
          <label class="block text-gray-700">{{ $t('an.fields.agent') }}</label>
          <select v-model="userDetails.SalesPerson" class="p93__input_general disabled:bg-gray-100" disabled>
            <option value="">{{ $t('an.actions.choose_agent') }}</option>
            <option v-for="agent in salesPeople" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
          </select>
          <div v-if="errors.SalesPerson" class="text-red-800 text-sm mt-1">{{ errors.SalesPerson }}</div>
        </div>
      </div>

      <div v-if="userDetails.Notes"  class="grid grid-cols-1 gap-4">
        <div class="mb-4 col-span-1">
          <label class="block text-gray-700">{{ $t('an.fields.customer_notes') }}</label>
          <textarea v-model="userDetails.Notes" class="p93__input" disabled readonly></textarea>
        </div>
      </div>
      <div class="flex justify-end">
        <button type="button" @click="closeUserDetailsModal" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2">{{ $t('an.actions.close') }}</button>
      </div>
      </div>
    </div>

  </div>
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Pagination from '@/Components/Pagination.vue';
import SortableTableHeader from '@/Components/SortableTableHeader.vue';

export default {
  props: ['users', 'errors', 'salesPeople', 'startAgent', 'fixedAgent'],
  components: {
    Link,
    BreadCrumbs,
    Pagination,
    SortableTableHeader
  },
  data() {
    return {
      showContatti: new URLSearchParams(window.location.search).get('showContatti') !== '0',
      showFornitori: new URLSearchParams(window.location.search).get('showFornitori') !== '0',
      sortField: new URLSearchParams(window.location.search).get('sort') || 'Contact',
      sortDirection: new URLSearchParams(window.location.search).get('direction') || 'desc',
      searchTerm: new URLSearchParams(window.location.search).get('search') || '',
      showUserModal: false,
      showUserDetailsModal: false,
      isEditing: false,
      foundPayments: [],
      userForm: useForm({
        id: null,
        CompanyName: '',
        City: '',
        Address: '',
        ZIPCode: '',
        County: '',
        ISOCountryCode: 'IT',
        Latitude: '',
        Longitude: '',
        Telephone1: '',
        Telephone2: '',
        EMail: '',
        TaxIdNumber: '',
        FiscalCode: '',
        Contact: '',
        SalesPerson: this.startAgent || ''
      }),
      userDetails: {},
    };
  },
  computed: {
    navigation() {
      return [
        { name: this.$t('ui.dashboard'), href: route('dashboard') },
        { name: this.$t('an.breadcrumb.anagrafiche'), href: route('modules.home', 'anagrafiche') },
        { name: this.$t('an.breadcrumb.contatti'), href: route('modules.anagrafiche.contatti') }
      ];
    },
  },
  methods: {
    sort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.performSearch();
    },
    handleContattiChange() {
      if (!this.showContatti && !this.showFornitori) {
        this.showFornitori = true;
      }
      this.performSearch();
    },
    handleFornitoriChange() {
      if (!this.showFornitori && !this.showContatti) {
        this.showContatti = true;
      }
      this.performSearch();
    },
    performSearch() {
      const params = { 
        search: this.searchTerm,
        sort: this.sortField || 'CompanyName',
        direction: this.sortDirection || 'asc'
      };

      router.get(route('modules.anagrafiche.contatti'), params, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      });
    },
    openAddUserModal() {
      this.userForm.reset();
      this.userForm.ISOCountryCode = 'IT';
      this.isEditing = false;
      this.showUserModal = true;
    },
    openEditUserModal(user) {
      this.userForm.reset();
      // Fetch detailed data for editing
      axios
        .get(route('modules.anagrafiche.contatti.dettagli-contatto') + '?contact=' + encodeURIComponent(user.Contact))
        .then((response) => {
          const data = response.data;
          this.userForm.reset();
          this.userForm.Contact = data.Contact || '';
          this.userForm.CompanyName = data.CompanyName || '';
          this.userForm.City = data.City || '';
          this.userForm.Address = data.Address || '';
          this.userForm.ZIPCode = data.ZIPCode || '';
          this.userForm.County = data.County || '';
          this.userForm.ISOCountryCode = data.ISOCountryCode || 'IT';
          this.userForm.Telephone1 = data.Telephone1 || '';
          this.userForm.Telephone2 = data.Telephone2 || '';
          this.userForm.EMail = data.EMail || '';
          this.userForm.TaxIdNumber = data.TaxIdNumber || '';
          this.userForm.FiscalCode = data.FiscalCode || '';
          this.userForm.SalesPerson = data.SalesPerson || '';
          this.isEditing = true;
          this.showUserModal = true;
        })
        .catch((error) => {
          console.error('Error fetching user details:', error);
        });
    },
    openDetailsUserModal(user) {
      axios
        .get(route('modules.anagrafiche.contatti.dettagli-contatto') + '?contact=' + encodeURIComponent(user.Contact))
        .then((response) => {
          this.userDetails = response.data;
          this.showUserDetailsModal = true;
        });
    },
    closeUserModal() {
      this.showUserModal = false;
    },
    closeUserDetailsModal() {
      this.showUserDetailsModal = false;
    },
    addUser() {
      // Prepara il payload, assicurandoti che Payment sia l'id
      this.userForm.Payment = this.userForm.Payment ? this.userForm.Payment.id : null;
      this.userForm.post(route('modules.anagrafiche.cust-supp.aggiungi-contatto'), {
        onSuccess: () => {
          this.closeUserModal();
          this.performSearch();
        }
      });
    },
    updateUser() {
      const queryParams = {
        search: this.searchTerm,
        sort: this.sortField || 'CompanyName',
        direction: this.sortDirection || 'asc',
        page: this.$page.props.users.current_page || 1
      };
      this.userForm.post(route('modules.anagrafiche.cust-supp.aggiorna-contatto', queryParams), {
        onSuccess: () => {
          this.closeUserModal();
        },
        preserveState: true,
      });
    }
  }
};
</script>
